import { z } from 'zod';
import { emailSchema } from '../ids.js';
import type { Language, MemberRole, PlatformRole, TaskPriority, Theme } from '../enums.js';

/**
 * Droits de la personne concernée, côté **praticien**.
 *
 * Le règlement ne distingue pas l'utilisateur du patient : un praticien dont on traite les données
 * a les mêmes droits d'accès et d'effacement. La V1 n'offrait ni l'un ni l'autre — aucune de ses
 * routes ne permettait à un utilisateur d'obtenir ses propres données, ni de demander leur
 * suppression.
 *
 * Ce que ces routes rendent est **ce qui appartient à l'utilisateur lui-même**. Jamais un dossier
 * patient : c'est la donnée d'un tiers, que le cabinet traite sous sa propre responsabilité, et
 * elle a sa route à elle. Jamais un secret non plus — ni empreinte de mot de passe, ni graine TOTP,
 * ni code de secours : un export les rendrait copiables, alors qu'ils ne servent qu'à prouver
 * l'identité.
 */

/* -------------------------------------------------------------------------- */
/* Entrées                                                                     */
/* -------------------------------------------------------------------------- */

/** Le code TOTP à six chiffres, exigé seulement si le second facteur est activé. */
const totpCodeSchema = z.string().regex(/^\d{6}$/, 'Code à six chiffres attendu');

/**
 * Ré-authentification, comme pour le changement d'adresse : le mot de passe, et le second facteur
 * s'il est activé. Elle voyage dans un corps de requête et non dans l'URL — un mot de passe en
 * paramètre finirait dans les journaux du serveur et dans l'historique du navigateur.
 */
export const accountReauthSchema = z
  .object({
    password: z.string().min(1).max(128),
    totpCode: totpCodeSchema.optional(),
  })
  .strict();
export type AccountReauthInput = z.infer<typeof accountReauthSchema>;

/**
 * Effacement du compte. L'adresse email est retapée en confirmation, comme le nom d'un dossier
 * patient : c'est irréversible, et un clic seul ne doit pas suffire.
 */
export const accountErasureSchema = accountReauthSchema
  .extend({ confirmEmail: emailSchema })
  .strict();
export type AccountErasureInput = z.infer<typeof accountErasureSchema>;

/* -------------------------------------------------------------------------- */
/* Sorties                                                                     */
/* -------------------------------------------------------------------------- */

/** Le compte lui-même. `totpEnabled` est un booléen : la graine ne sort jamais. */
export interface AccountExportProfileDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  platformRole: PlatformRole;
  isActive: boolean;
  emailVerifiedAt: string | null;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountExportPreferencesDto {
  theme: Theme;
  language: Language;
  /** Réglages d'affichage de l'agenda, tels qu'ils sont enregistrés. */
  agenda: Record<string, unknown>;
  /** Disposition du tableau de bord, telle qu'elle est enregistrée. */
  dashboardLayout: unknown;
}

/** Une appartenance à un cabinet, avec le profil praticien qui s'y rattache s'il existe. */
export interface AccountExportMembershipDto {
  memberId: string;
  organizationId: string;
  organizationName: string;
  role: MemberRole;
  isActive: boolean;
  joinedAt: string | null;
  createdAt: string;
  practitionerProfile: AccountExportPractitionerProfileDto | null;
}

export interface AccountExportPractitionerProfileDto {
  title: string | null;
  authorizationNumber: string | null;
  agendaColor: string | null;
  /** Vrai si une signature est enregistrée. L'image elle-même se télécharge à part. */
  hasSignature: boolean;
}

/**
 * Métadonnées d'une session : de quoi reconnaître ses propres connexions. Aucun jeton ni
 * empreinte de jeton — ce sont des clés d'accès, pas des données personnelles à restituer.
 */
export interface AccountExportSessionDto {
  id: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
}

/**
 * Une tâche personnelle, dans l'un des cabinets du compte. Le dossier rattaché n'y figure **que par
 * un booléen** : ni nom, ni identifiant. L'export quitte la plateforme sous forme de fichier, et le
 * patient est une tierce personne dont le cabinet est responsable ; son identifiant ne dirait rien
 * hors du cabinet, et le fait du rattachement suffit à restituer ce que le praticien a saisi.
 */
export interface AccountExportTaskDto {
  organizationId: string;
  label: string;
  description: string | null;
  priority: TaskPriority;
  dueDate: string | null;
  done: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  patientLinked: boolean;
}

export interface AccountExportDto {
  exportedAt: string;
  profile: AccountExportProfileDto;
  preferences: AccountExportPreferencesDto;
  memberships: AccountExportMembershipDto[];
  sessions: AccountExportSessionDto[];
  /** Tâches personnelles, tous cabinets confondus. */
  tasks: AccountExportTaskDto[];
}

/** Ce que l'effacement a réellement fait, pour que l'écran puisse le dire sans réinterpréter. */
export interface AccountErasedDto {
  userId: string;
  anonymizedAt: string;
  /** Appartenances désactivées : elles restent en base, les rendez-vous passés s'y rattachent. */
  deactivatedMemberships: number;
  /** Sessions révoquées, celle de l'appelant comprise. */
  revokedSessions: number;
  /**
   * Profils praticiens vidés : titre, numéro d'autorisation et signature enregistrée. Ce sont des
   * données personnelles, et les factures déjà émises sont des fichiers rangés au dossier qui se
   * relisent sans le profil.
   */
  erasedPractitionerProfiles: number;
  /** Tâches personnelles supprimées, tous cabinets confondus : aucune obligation ne les retient. */
  deletedTasks: number;
}
